// ============================================================
//  Editeur de carte — Leaflet image overlay + balises drag/drop.
//
//  Coords des balises = position normalisée 0..1 par rapport
//  a l'image (independant de la resolution).
// ============================================================

import { useEffect, useRef } from "react";
import L from "leaflet";
import type { Balise, Lieu } from "../lib/types";

export type MapEditorProps = {
  lieu: Lieu;
  imageUrl: string;
  balises: Balise[];
  selectedId: string | null;
  onCreate(pos: { x: number; y: number }): void;
  onMove(id: string, pos: { x: number; y: number }): void;
  onSelect(id: string | null): void;
};

export function MapEditor({
  lieu,
  imageUrl,
  balises,
  selectedId,
  onCreate,
  onMove,
  onSelect,
}: MapEditorProps) {
  const containerRef = useRef<HTMLDivElement>(null);
  const mapRef = useRef<L.Map | null>(null);
  const overlayRef = useRef<L.ImageOverlay | null>(null);
  const markersRef = useRef<Map<string, L.Marker>>(new Map());

  // Bornes "virtuelles" pour la carte (pixels de l'image).
  // Leaflet utilise CRS.Simple → on traite l'image comme un plan cartesien.
  const W = lieu.image_width ?? 1280;
  const H = lieu.image_height ?? 900;
  const bounds: L.LatLngBoundsExpression = [
    [0, 0],
    [H, W],
  ];

  // ---------- Initialisation Leaflet ----------
  useEffect(() => {
    if (!containerRef.current || mapRef.current) return;

    const map = L.map(containerRef.current, {
      crs: L.CRS.Simple,
      minZoom: -3,
      maxZoom: 2,
      zoomControl: true,
      attributionControl: false,
    });
    mapRef.current = map;

    overlayRef.current = L.imageOverlay(imageUrl, bounds).addTo(map);
    map.fitBounds(bounds);

    // Clic sur la carte = creer une balise
    map.on("click", (e: L.LeafletMouseEvent) => {
      const lat = e.latlng.lat; // 0..H
      const lng = e.latlng.lng; // 0..W
      const px = clamp01(lng / W);
      const py = clamp01(1 - lat / H);
      onCreate({ x: px, y: py });
    });

    return () => {
      map.remove();
      mapRef.current = null;
      overlayRef.current = null;
      markersRef.current.clear();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // ---------- Synchro des balises avec les markers ----------
  useEffect(() => {
    const map = mapRef.current;
    if (!map) return;

    const existing = markersRef.current;
    const seenIds = new Set<string>();

    for (const b of balises) {
      seenIds.add(b.id);
      const latlng = posToLatLng(b.position_x, b.position_y, W, H);
      let marker = existing.get(b.id);

      if (!marker) {
        marker = L.marker(latlng, {
          draggable: true,
          icon: makeIcon(b.ordre_suggere ?? null, b.id === selectedId),
        });
        marker.addTo(map);

        marker.on("click", () => onSelect(b.id));
        marker.on("dragend", () => {
          const ll = marker!.getLatLng();
          const x = clamp01(ll.lng / W);
          const y = clamp01(1 - ll.lat / H);
          onMove(b.id, { x, y });
        });

        existing.set(b.id, marker);
      } else {
        marker.setLatLng(latlng);
        marker.setIcon(makeIcon(b.ordre_suggere ?? null, b.id === selectedId));
      }
    }

    // Cleanup markers obsoletes
    for (const [id, marker] of existing) {
      if (!seenIds.has(id)) {
        marker.remove();
        existing.delete(id);
      }
    }
  }, [balises, selectedId, W, H, onMove, onSelect]);

  return (
    <div className="map-editor">
      <div ref={containerRef} className="map-canvas" />
    </div>
  );
}

// ---------- Helpers ----------

function clamp01(v: number): number {
  return Math.max(0, Math.min(1, v));
}

function posToLatLng(px: number, py: number, W: number, H: number): L.LatLngExpression {
  // px,py: 0..1 → image coords (0..W, H..0)
  return [H * (1 - py), W * px];
}

function makeIcon(ordre: number | null, selected: boolean): L.DivIcon {
  const label = ordre != null ? String(ordre) : "•";
  return L.divIcon({
    html: `<div class="balise-pin ${selected ? "selected" : ""}">${label}</div>`,
    className: "balise-pin-wrapper",
    iconSize: [32, 32],
    iconAnchor: [16, 32],
  });
}
